import { Badge, Box, Stack } from "@mui/material";
import { IconButton } from "@src/appV2/redesign/components/IconButton";
import { Text } from "@src/appV2/redesign/components/Text";
import { TimeSlotAvatar } from "@src/appV2/redesign/Shift/TimeSlotAvatar";
import { type TimeSlotWithExtras } from "@src/appV2/Shifts/types";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { format } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { useTu } from "@src/appV2/i18n/useTu";

type TimeFormat = "12h" | "24h";

interface ShiftPostingHeaderProps {
  timeSlot: TimeSlotWithExtras;
  startTime: Date;
  endTime: Date;
  qualificationDisplayName: string;
  timezone: string;
  onEditTime: () => void;
  /**
   * Time format for display. Defaults to "12h".
   */
  timeFormat?: TimeFormat;
  hasCustomTime?: boolean;
}

function formatTimeRange(
  startTime: Date,
  endTime: Date,
  timeFormat: TimeFormat,
  timezone?: string
): string {
  if (timeFormat === "24h") {
    const start = format(startTime, "HH:mm");
    const end = format(endTime, "HH:mm");
    return `${start} - ${end}`;
  }

  const formatString = "h:mmaaa";

  if (timezone) {
    const start = formatInTimeZone(startTime, timezone, formatString);
    const end = formatInTimeZone(endTime, timezone, formatString);
    return `${start} - ${end}`;
  }

  const start = format(startTime, formatString);
  const end = format(endTime, formatString);
  return `${start} - ${end}`;
}

/**
 * Header section for the ShiftPostingForm.
 * Displays time slot avatar, time range, qualification text, and edit button.
 *
 * Based on Figma design:
 * https://www.figma.com/design/DuWNxZH8A10yrLwq3dedDa/Daily-view---dev-ready?node-id=765-6086
 */
export function ShiftPostingHeader(props: ShiftPostingHeaderProps) {
  const { tu } = useTu("schedule");
  const {
    timeSlot,
    startTime,
    endTime,
    qualificationDisplayName,
    timezone,
    onEditTime,
    timeFormat = "12h",
    hasCustomTime = false,
  } = props;

  const formattedTimeRange = formatTimeRange(startTime, endTime, timeFormat, timezone);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: "10px",
      }}
    >
      <TimeSlotAvatar timeSlot={timeSlot} />

      <Stack
        spacing={0}
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Text
          semibold
          variant="body2"
          sx={(theme) => ({
            color: theme.palette.text.primary,
            fontSize: theme.typography?.body2?.fontSize,
            lineHeight: "normal",
          })}
        >
          {formattedTimeRange}
        </Text>
        <Text
          semibold
          variant="caption"
          sx={(theme) => ({
            color: theme.palette.text.secondary,
            fontSize: theme.typography?.caption?.fontSize,
            lineHeight: "normal",
          })}
        >
          {qualificationDisplayName}
        </Text>
      </Stack>

      <Badge color="primary" invisible={!hasCustomTime} overlap="circular" variant="dot">
        <IconButton
          circular
          aria-label={hasCustomTime ? tu("Edit time (custom)") : tu("Edit time")}
          iconSize="large"
          iconType="pencil"
          size="medium"
          variant="outlined"
          onClick={onEditTime}
        />
      </Badge>
    </Box>
  );
}
